import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Cell, Tag, Loading, Empty, Tabs } from 'react-vant'
import { ArrowLeft, Records } from '@react-vant/icons'
import request from '../../api/request'

export default function StudentDetail() {
    const { studentId } = useParams()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [student, setStudent] = useState(null)
    const [tests, setTests] = useState([])

    const formatYear = (yr) => {
        if (!yr) return '';
        if (typeof yr !== 'string') return `${yr}学年`;
        if (yr.includes('学年')) return yr;
        const match = yr.match(/^(\d{4}-\d{4})(.*)$/);
        if (match) {
            return `${match[1]}学年${match[2]}`;
        }
        return `${yr}学年`;
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            try {
                const [studentRes, testsRes] = await Promise.all([
                    request.get(`/academic/students/${studentId}`),
                    request.get(`/sports/tests/`, { params: { student_id: studentId, page_size: 50 } })
                ])
                setStudent(studentRes)
                setTests(testsRes.items || [])
            } catch (e) {
                console.error('获取学生详情失败', e)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [studentId])

    const getScoreColor = (score) => {
        if (score >= 90) return '#00b578'
        if (score >= 80) return '#056b4f'
        if (score >= 60) return '#d97706'
        return '#e11d48'
    }

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Loading type="spinner">加载中...</Loading>
            </div>
        )
    }

    if (!student) {
        return (
            <div className="page-container" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div style={{ padding: '60px 16px', background: '#fff' }}>
                    <div onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', color: '#056b4f', marginBottom: 20 }}>
                        <ArrowLeft /> <span>返回</span>
                    </div>
                    <Empty description="学生不存在" />
                </div>
            </div>
        )
    }

    return (
        <div className="student-detail-page" style={{ background: '#f8fafc', minHeight: '100vh', paddingBottom: 40 }}>
            {/* 顶层沉浸式头部 */}
            <div style={{
                background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)',
                padding: '60px 16px 60px',
                color: '#fff',
                position: 'relative'
            }}>
                <div style={{ position: 'absolute', left: 16, top: 44, display: 'flex', alignItems: 'center' }} onClick={() => navigate(-1)}>
                    <ArrowLeft size={24} />
                    <span style={{ marginLeft: 4, fontSize: 16 }}>返回</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', marginTop: 20 }}>
                    <div style={{
                        width: 64, height: 64, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 28, fontWeight: 'bold', border: '3px solid rgba(255,255,255,0.1)',
                        marginRight: 16
                    }}>
                        {student.real_name?.[0]}
                    </div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{student.real_name}</h2>
                            <Tag plain style={{ marginLeft: 8, color: '#fff', border: '1px solid rgba(255,255,255,0.5)', background: 'transparent' }}>
                                {student.gender === 'male' ? '男' : '女'}
                            </Tag>
                        </div>
                        <div style={{ fontSize: 13, opacity: 0.8 }}>
                            {student.class_name || '未分配班级'} | 学号: {student.student_no}
                        </div>
                    </div>
                </div>

                <div style={{
                    position: 'absolute', bottom: -30, left: 16, right: 16,
                    background: '#fff', borderRadius: 12, padding: '16px 0',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.06)',
                    display: 'flex', justifyContent: 'space-around', textAlign: 'center'
                }}>
                    <div>
                        <div style={{ fontSize: 18, fontWeight: 'bold', color: '#056b4f', marginBottom: 2 }}>{student.grade || '未知'}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>当前年级</div>
                    </div>
                    <div style={{ width: 1, background: '#f1f5f9' }}></div>
                    <div>
                        <div style={{ fontSize: 18, fontWeight: 'bold', color: '#00b578', marginBottom: 2 }}>{tests.length}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>体测记录</div>
                    </div>
                    <div style={{ width: 1, background: '#f1f5f9' }}></div>
                    <div>
                        <div style={{ fontSize: 18, fontWeight: 'bold', color: '#d97706', marginBottom: 2 }}>{student.health_status || '优秀'}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>健康状况</div>
                    </div>
                </div>
            </div>

            <div style={{ marginTop: 45 }}>
                <Tabs active={0} sticky offsetTop={0} background="transparent" color="#056b4f">
                    <Tabs.TabPane title="档案信息">
                        <Cell.Group inset style={{ marginTop: 16 }}>
                            <Cell title="教育ID" titleStyle={{ color: '#94a3b8' }} value={student.education_id || '-'} />
                            <Cell title="民族" titleStyle={{ color: '#94a3b8' }} value={student.ethnicity || '汉族'} />
                            <Cell title="出生日期" titleStyle={{ color: '#94a3b8' }} value={student.birth_date} />
                            <Cell title="学籍号" titleStyle={{ color: '#94a3b8' }} value={student.student_no} />
                        </Cell.Group>

                        {(student.allergy_info || student.special_notes) && (
                            <Cell.Group inset style={{ marginTop: 12 }}>
                                {student.allergy_info && <Cell title="过敏史" label={student.allergy_info} />}
                                {student.special_notes && <Cell title="特别备注" label={student.special_notes} />}
                            </Cell.Group>
                        )}
                    </Tabs.TabPane>

                    <Tabs.TabPane title="体测记录">
                        <div style={{ padding: '0 12px', marginTop: 16 }}>
                            {tests.length > 0 ? (
                                tests.map(test => (
                                    <div
                                        key={test.id}
                                        onClick={() => navigate(`/physical-test/detail/${test.id}`)}
                                        style={{
                                            background: '#fff', borderRadius: 12, padding: 16, marginBottom: 12,
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <div style={{
                                                width: 40, height: 40, borderRadius: 8,
                                                background: '#f0fdf4', display: 'flex',
                                                alignItems: 'center', justifyContent: 'center',
                                                marginRight: 12
                                            }}>
                                                <Records color="#056b4f" size={20} />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 15, fontWeight: 500, color: '#0f172a' }}>{formatYear(test.academic_year)}</div>
                                                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{test.test_date}</div>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: 20, fontWeight: 700, color: getScoreColor(test.total_score) }}>
                                                {test.total_score}
                                            </div>
                                            <Tag size="mini" type={test.grade_name === '优秀' ? 'success' : (test.grade_name === '不及格' ? 'danger' : 'primary')} plain>
                                                {test.grade_name}
                                            </Tag>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <Empty description="暂无体测记录" />
                            )}
                        </div>
                    </Tabs.TabPane>
                </Tabs>
            </div>
        </div>
    )
}
